// AiChatService.ts
import CONFIG from "config/config";

export default class AiChatService {
  private apiToken: string | null;
  private baseURL: string;

  constructor(apiToken: string | null = null) {
    this.apiToken = apiToken;
    this.baseURL = CONFIG.AI_CHAT_FASTAPI || 'http://localhost:8001/api/v1';
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}, sessionId?: string) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {})
    };

    if (this.apiToken) {
      headers['Authorization'] = "Token " + this.apiToken;
    }

    if (sessionId) {
      headers['session-id'] = sessionId;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Session management
  async createSession() {
    return this.makeRequest('/chatbot/session', {
      method: 'POST'
    });
  }

  async getUserSessions(limit: number = 20) {
    return this.makeRequest(`/chatbot/sessions?limit=${limit}`);
  }

  async activateSession(sessionId: string) {
    return this.makeRequest(`/chatbot/session/${sessionId}/activate`, {
      method: 'POST'
    });
  }

  async deactivateSession(sessionId: string) {
    return this.makeRequest(`/chatbot/session/${sessionId}`, {
      method: 'DELETE'
    });
  }

  // Patient and context
  async searchPatient(sessionId: string, hn: string) {
    return this.makeRequest('/chatbot/patient', {
      method: 'POST',
      body: JSON.stringify({ hn })
    }, sessionId);
  }

  async getCurrentPatientContext(sessionId: string) {
    return this.makeRequest('/chatbot/patient-context', {}, sessionId);
  }

  async sendMessageStream(sessionId: string, message: string, onChunk: (chunk: any) => void) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (this.apiToken) {
      headers['Authorization'] = "Token " + this.apiToken;
    }

    if (sessionId) {
      headers['session-id'] = sessionId;
    }

    try {
      const response = await fetch(`${this.baseURL}/chatbot/chat/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete chunks
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              onChunk(data);
            } catch (e) {
              console.warn('Failed to parse chunk:', line);
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      throw error;
    }
  }

  // Chat
  async sendMessage(sessionId: string, message: string) {
    return this.makeRequest('/chatbot/chat', {
      method: 'POST',
      body: JSON.stringify({ message })
    }, sessionId);
  }

  async getChatHistory(sessionId: string, limit: number = 50) {
    return this.makeRequest(`/chatbot/history?limit=${limit}`, {}, sessionId);
  }

  async clearHistory(sessionId: string) {
    return this.makeRequest('/chatbot/history', {
      method: 'DELETE'
    }, sessionId);
  }

  async summarizePatientStream(sessionId: string, onChunk: (chunk: any) => void) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (this.apiToken) {
      headers['Authorization'] = "Token " + this.apiToken;
    }

    if (sessionId) {
      headers['session-id'] = sessionId;
    }

    try {
      const response = await fetch(`${this.baseURL}/chatbot/summarize/stream`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete chunks
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              onChunk(data);
            } catch (e) {
              console.warn('Failed to parse summary chunk:', line);
            }
          }
        }
      }
    } catch (error) {
      console.error('Summary streaming error:', error);
      throw error;
    }
  }

  // Summarize
  async summarizePatient(sessionId: string) {
    return this.makeRequest('/chatbot/summarize', {}, sessionId);
  }
}